/**
 * WasteTable component
 * Displays waste logged per session for end-of-day summary
 * Shows waste breakdown by type for each session
 */
import React, { useState } from 'react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { SessionWasteBreakdown } from '../../../../services/productionService';
import { FP_NI_WASTE_ROW_CLASSES } from '../../../Inventory/inventoryHelpers';
import { Trash2, Recycle, ChevronDown, ChevronRight } from 'lucide-react';

interface WasteTableProps {
  wasteBySession?: SessionWasteBreakdown[];
}

// Waste type display names and colors
const wasteTypeConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  by_product: { label: 'By-Product', color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
  by_product_marketable: { label: 'Byproduct - Marketable', color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
  by_product_production: { label: 'Byproduct - Production', color: 'text-teal-600', bgColor: 'bg-teal-100 dark:bg-teal-900/30' },
  disposable: { label: 'Disposable', color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-900/30' },
  water_liquid: { label: 'Water/Liquid', color: 'text-cyan-600', bgColor: 'bg-cyan-100 dark:bg-cyan-900/30' },
  human_error: { label: 'Human Error', color: 'text-orange-600', bgColor: 'bg-orange-100 dark:bg-orange-900/30' },
  remainder_intact: { label: 'Remainder (Intact)', color: 'text-purple-600', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
  remainder_not_intact: { label: 'Remainder (Not Intact)', color: 'text-pink-600', bgColor: 'bg-pink-100 dark:bg-pink-900/30' },
  finish_product_not_intact: { label: 'Finish Product (Not Intact)', color: FP_NI_WASTE_ROW_CLASSES.color, bgColor: FP_NI_WASTE_ROW_CLASSES.bgColor },
};

// Actual waste (counted in Total Waste): real waste + byproduct (per WCO-2268).
const ACTUAL_WASTE_TYPES = ['disposable', 'human_error', 'water_liquid', 'by_product', 'by_product_marketable', 'by_product_production'];
// Production Remainder (NOT counted as waste): FP-NI + raw-material remainders — recoverable.
const PRODUCTION_REMAINDER_TYPES = ['finish_product_not_intact', 'remainder_intact', 'remainder_not_intact'];

const WasteTable: React.FC<WasteTableProps> = ({ wasteBySession = [] }) => {
  const { t } = useLanguage();
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());

  // Actual-waste-only total for a session (excludes Production Remainder: FP-NI + remainders).
  const actualWasteOf = (s: SessionWasteBreakdown) =>
    s.waste_types.filter(wt => ACTUAL_WASTE_TYPES.includes(wt.waste_type)).reduce((sum, wt) => sum + wt.weight, 0);

  // Total Waste across all sessions = Actual Waste only.
  const totalWaste = wasteBySession.reduce((sum, s) => sum + actualWasteOf(s), 0);

  const toggleSession = (sessionId: string) => {
    setExpandedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  if (wasteBySession.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <Trash2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>{t('endOfDaySummary.noWasteLogged')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with total */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {t('endOfDaySummary.wasteBySession')}
          </h3>
        </div>
        <div className="text-sm font-semibold text-gray-900 dark:text-white">
          {t('endOfDaySummary.totalWaste')}: {totalWaste.toFixed(2)} lb
        </div>
      </div>

      {/* Sessions list */}
      <div className="space-y-2">
        {wasteBySession.map((session) => {
          const isExpanded = expandedSessions.has(session.session_number);

          return (
            <div
              key={session.session_number}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              {/* Session header - clickable to expand */}
              <button
                onClick={() => toggleSession(session.session_number)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                  <div className="text-left">
                    <span className="text-sm font-medium text-green-600 dark:text-green-400">
                      {session.session_number}
                    </span>
                    <span className="mx-2 text-gray-400">·</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {session.product_name}
                    </span>
                    {session.product_chinese_name && (
                      <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                        {session.product_chinese_name}
                      </span>
                    )}
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      ({session.product_sku})
                    </span>
                  </div>
                </div>
                <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                  {actualWasteOf(session).toFixed(2)} lb
                </span>
              </button>

              {/* Expanded breakdown - two columns: Actual Waste | Production Remainder */}
              {isExpanded && (() => {
                const wasteItems = session.waste_types.filter(wt => ACTUAL_WASTE_TYPES.includes(wt.waste_type));
                const remainderItems = session.waste_types.filter(wt => PRODUCTION_REMAINDER_TYPES.includes(wt.waste_type));
                const wasteSubtotal = wasteItems.reduce((sum, wt) => sum + wt.weight, 0);
                const remainderSubtotal = remainderItems.reduce((sum, wt) => sum + wt.weight, 0);

                const renderRow = (wt: typeof session.waste_types[0], idx: number, total: number, groupSubtotal: number, muted: boolean) => {
                  const config = wasteTypeConfig[wt.waste_type] || { label: wt.waste_type, color: 'text-gray-600', bgColor: 'bg-gray-100' };
                  const isFpNi = wt.waste_type === 'finish_product_not_intact';
                  const labelMuted = muted && !isFpNi;
                  // Byproducts carry an item id/name so each one lists separately.
                  const label = wt.byproduct_item
                    ? `${config.label}  ${wt.byproduct_item}${wt.byproduct_item_name ? ' - ' + wt.byproduct_item_name : ''}`
                    : config.label;
                  const percentage = groupSubtotal > 0 ? (wt.weight / groupSubtotal) * 100 : 0;
                  return (
                    <div
                      key={`${wt.waste_type}-${wt.byproduct_item || ''}-${idx}`}
                      className={`flex items-center justify-between px-3 py-1.5 ${
                        idx < total - 1 ? 'border-b border-gray-100 dark:border-gray-700/50' : ''
                      }`}
                    >
                      <span className={`text-sm ${labelMuted ? 'text-gray-400 dark:text-gray-500' : config.color}`}>{label}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-400 dark:text-gray-500">{percentage.toFixed(2)}%</span>
                        <span className={`text-sm font-semibold w-20 text-right ${labelMuted ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>{wt.weight.toFixed(2)} lb</span>
                      </div>
                    </div>
                  );
                };

                return (
                  <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 grid grid-cols-2 divide-x divide-gray-200 dark:divide-gray-700">
                    {/* Left column - Actual Waste (counted) */}
                    <div>
                      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-200 dark:border-gray-700">
                        <Trash2 className="w-4 h-4 text-red-500" />
                        <span className="text-sm font-bold text-red-600 dark:text-red-400">{t('operator.actualWaste')}</span>
                        <span className="ml-auto text-sm font-bold text-red-600 dark:text-red-400">{wasteSubtotal.toFixed(2)} lb</span>
                      </div>
                      {wasteItems.length > 0
                        ? wasteItems.map((wt, idx) => renderRow(wt, idx, wasteItems.length, wasteSubtotal, false))
                        : <div className="px-3 py-2 text-xs text-gray-400">—</div>
                      }
                    </div>
                    {/* Right column - Production Remainder (NOT counted as waste, greyed) */}
                    <div>
                      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-200 dark:border-gray-700">
                        <Recycle className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-bold text-gray-400 dark:text-gray-500">{t('operator.productionRemainder')}</span>
                        <span className="ml-auto text-sm font-bold text-gray-400 dark:text-gray-500">{remainderSubtotal.toFixed(2)} lb</span>
                      </div>
                      {remainderItems.length > 0
                        ? remainderItems.map((wt, idx) => renderRow(wt, idx, remainderItems.length, remainderSubtotal, true))
                        : <div className="px-3 py-2 text-xs text-gray-400">—</div>
                      }
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WasteTable;
